/** Feature flags — master and transport default on; explicit false remains the kill switch. */

/**
 * Runtime env-config injected by the deploy pipeline (env-config.js →
 * `window.__ENV__`). Reading it here — rather than only `import.meta.env` —
 * lets ops flip the funnel UI event flags at deploy time (or by editing the
 * S3 env-config.js) WITHOUT rebuilding the storefront bundle.
 */
interface RuntimeEnvWindow {
  __ENV__?: Record<string, string>;
}

function getRuntimeEnv(): Record<string, string> | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as RuntimeEnvWindow).__ENV__;
}

function isTruthyFlag(value: string | undefined | null): boolean {
  return value === 'true' || value === '1';
}

function isFalsyFlag(value: string | undefined | null): boolean {
  return value === 'false' || value === '0';
}

/**
 * Resolve a boolean funnel flag with precedence (first decisive wins):
 *   1. URL query param — per-session override for QA/debugging.
 *   2. `window.__ENV__[envKey]` — runtime deploy config (flippable in S3).
 *   3. `import.meta.env[envKey]` — build-time fallback (passed by the caller
 *      so Vite can statically replace the literal access).
 * Default ON (owner decision 2026-07-15): absent an explicit "false" at any
 * layer, the flag resolves true. Kill switches: `?funnel_ui_events=0` per
 * session, or `false` in env-config.js (S3, no rebuild) / the deploy env.
 */
function resolveFunnelFlag(opts: {
  queryParam: string;
  envKey: string;
  buildValue: string | undefined;
  /** Fallback when no layer decides. Master/transport default ON; debug defaults OFF. */
  defaultValue?: boolean;
}): boolean {
  if (typeof window !== 'undefined') {
    try {
      const param = new URLSearchParams(window.location.search).get(opts.queryParam);
      if (isTruthyFlag(param)) return true;
      if (isFalsyFlag(param)) return false;
    } catch {
      /* malformed location.search — fall through to env config */
    }
  }

  const runtimeValue = getRuntimeEnv()?.[opts.envKey];
  if (isTruthyFlag(runtimeValue)) return true;
  if (isFalsyFlag(runtimeValue)) return false;

  if (isTruthyFlag(opts.buildValue)) return true;
  if (isFalsyFlag(opts.buildValue)) return false;

  // Default ON — owner decision 2026-07-15 (previously off during the e2e gate).
  // Debug visibility is the exception: it defaults OFF and is turned on per
  // environment (dev) by the deploy env-config.
  return opts.defaultValue ?? true;
}

/** Master switch for the funnel UI event system (ADR 0004). */
export function isFunnelUiEventsEnabled(): boolean {
  return resolveFunnelFlag({
    queryParam: 'funnel_ui_events',
    envKey: 'VITE_FUNNEL_UI_EVENTS',
    buildValue: import.meta.env?.VITE_FUNNEL_UI_EVENTS,
  });
}

/**
 * Ships emitted events to the BFF telemetry ingest. Independent from the master
 * switch: events can be produced/consumed in-app with transport off.
 */
/**
 * Dev-only event visibility: when ON, every emitted funnel event (plus listener
 * matches, reactions and wire transitions) is console.log'd so authors/devs can
 * SEE events firing. Defaults OFF; the deploy env-config turns it on for dev
 * (`FUNNEL_UI_EVENTS_DEBUG`), and `?funnel_ui_events_debug=1` overrides per
 * session anywhere (incl. prod ad-hoc debugging).
 */
export function isFunnelUiEventsDebugEnabled(): boolean {
  return resolveFunnelFlag({
    queryParam: 'funnel_ui_events_debug',
    envKey: 'VITE_FUNNEL_UI_EVENTS_DEBUG',
    buildValue: import.meta.env?.VITE_FUNNEL_UI_EVENTS_DEBUG,
    defaultValue: false,
  });
}

export function isFunnelUiEventsTransportEnabled(): boolean {
  return resolveFunnelFlag({
    queryParam: 'funnel_ui_events_transport',
    envKey: 'VITE_FUNNEL_UI_EVENTS_TRANSPORT',
    buildValue: import.meta.env?.VITE_FUNNEL_UI_EVENTS_TRANSPORT,
  });
}

/**
 * Controls cart-bound funnel acceptance through the signed pricing-proof path.
 *
 * This gate intentionally defaults OFF. A deployment must opt in only after the
 * carts proof consumer, storefront BFF issuer, and their infrastructure are
 * healthy. Turning it off suppresses commerce CTAs and makes every remaining
 * programmatic entry point fail closed; it never restores the legacy unproved
 * cart mutation path.
 */
export function isFunnelSecureCartProofEnabled(): boolean {
  return resolveFunnelFlag({
    queryParam: 'funnel_secure_cart_proof',
    envKey: 'VITE_FUNNEL_SECURE_CART_PROOF',
    buildValue: import.meta.env?.VITE_FUNNEL_SECURE_CART_PROOF,
    defaultValue: false,
  });
}
